import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNavGuard } from '@/shared/store/nav-guard';

/**
 * Shell-level "unsaved changes" confirm. Opens when a shell nav trigger
 * (SideNav rail, profile menu) tries to leave a surface that registered
 * itself as blocking via `useNavGuard` — currently the patient Add/Edit page.
 *
 * Copy matches the in-page Back-button dialog (`CancelChangesDialog`) and
 * Flutter `cancelCreatingDialog` (create_patient_screen.dart:249-261):
 *   title:   "Do you really want to cancel?"
 *   message: "Your progress will be discarded and the changes will not be saved."
 *
 * Destructive Discard (Flutter `isRemove: true`); "Keep editing" dismisses.
 * Mounted once at app root (see App.tsx).
 */
export function UnsavedChangesGuardDialog() {
  const pending = useNavGuard((s) => s.pending);
  // A surface that shows its own confirm (the automation editor) owns the prompt.
  const ownedElsewhere = useNavGuard((s) => s.dialog === 'owned');
  const confirm = useNavGuard((s) => s.confirm);
  const cancel = useNavGuard((s) => s.cancel);

  return (
    <AlertDialog
      open={pending !== null && !ownedElsewhere}
      onOpenChange={(open) => {
        // Escape / outside-click closes → treat as "keep editing".
        if (!open) cancel();
      }}
    >
      <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <AlertDialogHeader>
          <AlertDialogTitle>Do you really want to cancel?</AlertDialogTitle>
          <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
            Your progress will be discarded and the changes will not be saved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={cancel}>Keep editing</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={confirm}>
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default UnsavedChangesGuardDialog;
